import React, { createContext, useContext, useCallback, useEffect, useRef, useState } from "react";
import { api, setToken, getToken } from "@/lib/api";
import { queryClient } from "@/lib/query";

const Ctx = createContext(null);
export const useAuth = () => useContext(Ctx);

const sentSessionIds = new Set();

// Cache the signed-in user so a cold start / PWA resume can restore the session
// instantly without flashing the login screen while /auth/me revalidates.
const USER_CACHE_KEY = "td_user";
function readCachedUser() {
  try { const r = localStorage.getItem(USER_CACHE_KEY); return r ? JSON.parse(r) : null; } catch { return null; }
}
function clearCachedUser() {
  try { localStorage.removeItem(USER_CACHE_KEY); } catch { /* ignore */ }
}

// Load the effective feature flags (platform toggles + per-user overrides) whenever
// the signed-in identity changes (login, switch account, refresh).
function useFeatureFlags(userId) {
  const [features, setFeatures] = useState(null);
  const [planLocked, setPlanLocked] = useState([]);
  const [comingSoon, setComingSoon] = useState([]);
  useEffect(() => {
    if (!userId) { setFeatures(null); setPlanLocked([]); setComingSoon([]); return; }
    let alive = true;
    let attempt = 0;
    const load = () => {
      api("/features/me").then((r) => {
        if (!alive) return;
        setFeatures(r.features || null); setPlanLocked(r.plan_locked || []); setComingSoon(r.coming_soon || []);
      }).catch(() => {
        // Retry transient failures so a dropped call can't leave the sidebar stuck empty.
        if (alive && attempt < 3) { attempt += 1; setTimeout(load, 1000 * attempt); }
      });
    };
    load();
    return () => { alive = false; };
  }, [userId]);
  return { features, planLocked, comingSoon };
}

// Presence heartbeat: mark me active so others see me online, but pause while idle
// so the heartbeat can't keep an abandoned session alive on a shared device.
// Persistent login: sessions are NEVER auto-logged-out on the client, only an explicit
// Sign Out (or backend deactivation / admin 7-day idle) ends a session.
function usePresenceHeartbeat(userId) {
  const lastActivityRef = useRef(Date.now());
  useEffect(() => {
    if (!userId) return;
    const ping = () => {
      if (Date.now() - lastActivityRef.current > 5 * 60 * 1000) return;
      api("/presence/ping", { method: "POST" }).catch(() => {});
    };
    ping();
    const t = setInterval(ping, 60000);
    return () => clearInterval(t);
  }, [userId]);
  useEffect(() => {
    if (!userId) return;
    const bump = () => { lastActivityRef.current = Date.now(); };
    const events = ["mousedown", "keydown", "touchstart", "scroll", "mousemove"];
    events.forEach((e) => window.addEventListener(e, bump, { passive: true }));
    return () => { events.forEach((e) => window.removeEventListener(e, bump)); };
  }, [userId]);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // "Preview as member": lets an admin/owner temporarily see the plain member
  // experience (admin + business-owner chrome hidden, admin/owner pages blocked).
  const [previewMember, setPreviewMemberState] = useState(() => {
    try { return localStorage.getItem("td_preview_member") === "1"; } catch { return false; }
  });
  const setPreviewMember = useCallback((v) => {
    setPreviewMemberState(!!v);
    try {
      if (v) localStorage.setItem("td_preview_member", "1");
      else localStorage.removeItem("td_preview_member");
    } catch { /* ignore storage errors */ }
  }, []);
  const { features, planLocked, comingSoon } = useFeatureFlags(user?.user_id);
  usePresenceHeartbeat(user?.user_id);

  // Keep the cached user in sync so resume/reload can restore it instantly.
  useEffect(() => {
    if (user) {
      try { localStorage.setItem(USER_CACHE_KEY, JSON.stringify(user)); } catch { /* ignore */ }
    }
  }, [user]);

  const bootstrap = useCallback(async () => {
    const t = getToken();
    if (!t) {
      setUser(null);
      clearCachedUser();
      setLoading(false);
      return;
    }
    // Optimistically restore the cached user so an app resume / reload never flashes
    // the login screen while we revalidate in the background.
    const cached = readCachedUser();
    if (cached) {
      setUser(cached);
      setLoading(false);
    }
    try {
      const { user } = await api("/auth/me");
      setUser(user);
    } catch (e) {
      // ONLY sign the user out on a definitive auth rejection. Transient failures
      // (offline, timeout, PWA resume before network is ready, server hiccup) must
      // NEVER wipe a valid session — that was the "logged out on app switch" bug.
      if (e.status === 401 || e.status === 403) {
        setToken(null);
        clearCachedUser();
        setUser(null);
      }
      // else: keep the token and the cached user; the next successful call recovers.
    } finally {
      setLoading(false);
    }
  }, []);

  const exchangeSession = useCallback(async (sessionId) => {
    if (sentSessionIds.has(sessionId)) return;
    sentSessionIds.add(sessionId);
    const { session_token, user } = await api("/auth/session", {
      method: "POST",
      body: { session_id: sessionId },
      auth: false,
    });
    setToken(session_token);
    setUser(user);
  }, []);

  useEffect(() => {
    const hay = window.location.hash + window.location.search;
    const m = hay.match(/[?#&]session_id=([^&#]+)/);
    if (m) {
      setLoading(true);
      exchangeSession(decodeURIComponent(m[1]))
        .then(() => {
          window.history.replaceState(window.history.state, "", window.location.pathname);
        })
        .catch(() => {})
        .finally(() => setLoading(false));
    } else {
      bootstrap();
    }
  }, [bootstrap, exchangeSession]);

  const login = async (email, password) => {
    const { session_token, user, restored } = await api("/auth/login", {
      method: "POST",
      body: { email, password },
      auth: false,
    });
    setToken(session_token);
    setUser(user);
    return { restored };
  };

  const register = async (email, password, fullName, extra = {}) => {
    const { session_token, user } = await api("/auth/register", {
      method: "POST",
      body: { email, password, full_name: fullName, ...extra },
      auth: false,
    });
    setToken(session_token);
    setUser(user);
  };

  const googleLogin = (next = "/") => {
    // Return the user to where they were headed (e.g. /team-chat for the chat PWA),
    // defaulting to home. Only allow safe internal paths to avoid open redirects.
    const safeNext = typeof next === "string" && next.startsWith("/") && !next.startsWith("//") ? next : "/";
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + safeNext;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try {
      await api("/auth/logout", { method: "POST" });
    } catch (e) { console.warn("Logout request failed (clearing session anyway):", e); }
    setToken(null);
    setUser(null);
    clearCachedUser();
    setPreviewMember(false); // never carry a member-preview across sessions
    queryClient.clear(); // drop the previous member's cached responses
  };

  const refresh = async () => {
    try {
      const { user } = await api("/auth/me");
      setUser(user);
    } catch (e) { console.warn("Session refresh failed:", e); }
  };

  const switchAccount = async (targetUserId) => {
    const { session_token, user } = await api("/accounts/switch", {
      method: "POST",
      body: { target_user_id: targetUserId },
    });
    setToken(session_token);
    setUser(user);
    setPreviewMember(false); // a freshly switched account starts in its real role
    return user;
  };

  // Pro-tier entitlement, mirroring backend core.is_pro_entitled (Pro plan, complimentary Pro,
  // Business accounts, Brand Partners, and admins are all entitled). Free personal members are not.
  const isPro = !!user && (
    user.is_pro || user.comp_pro ||
    user.account_type === "business" ||
    user.selected_plan === "brand_partner" ||
    user.role === "admin"
  );

  // Business Suite entitlement: Business accounts, Brand Partners, and Two Degrees
  // Admins. Regular personal members are NOT entitled (used to gate Projects, Docs,
  // Calendar, My HR and other Business Suite modules).
  const realIsAdmin = !!user && (!!user.is_admin || (user.role === "admin" && user.account_type !== "business"));
  const realBusinessSuite = !!user && (
    user.account_type === "business" ||
    user.selected_plan === "brand_partner" ||
    realIsAdmin
  );
  // Who may use "Preview as member": any admin or business-owner account.
  const canPreview = realBusinessSuite;
  // Effective (preview-aware) flags — these are what the whole app consumes, so
  // toggling preview instantly hides every admin/owner affordance.
  const isAdmin = realIsAdmin && !previewMember;
  const isBusinessSuite = realBusinessSuite && !previewMember;

  return (
    <Ctx.Provider value={{ user, loading, features, planLocked, comingSoon, isPro, isBusinessSuite, isAdmin, realIsAdmin, canPreview, previewMember, setPreviewMember, login, register, googleLogin, logout, refresh, setUser, switchAccount }}>
      {children}
    </Ctx.Provider>
  );
}
